import { Icon20CalendarOutline, Icon20GovernmentOutline, Icon20Hearts2Outline, Icon20SmileAddOutline } from '@vkontakte/icons';
import { classNames, Headline, Spacing } from '@vkontakte/vkui';
import image from 'img/family-by-option/img.png';
import React, { type RefObject, forwardRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { isRebrandingQuotaLanding } from 'reactApp/appHelpers/featuresHelpers';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import type { FamilyByOptionTooltipProps } from 'reactApp/sections/QuotaLanding/FamilyByOptionTooltip/FamilyByOptionTooltip.types';
import { sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipAppearance, ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './FamilyByOptionTooltip.css';

// текстовки в зависимости от платформы
const TEXTS = {
    desktop: {
        title: (
            <>
                Делитесь гигабайтами
                <br />и функциями с близкими
            </>
        ),
        items: [
            { icon: Icon20SmileAddOutline, text: 'Добавление ещё 3 участников' },
            { icon: Icon20Hearts2Outline, text: 'Семейные папки и альбомы' },
            { icon: Icon20GovernmentOutline, text: 'Распознавание документов семьи' },
        ],
    },
    mobile: {
        title: (
            <>
                Подключите одну подписку
                <br />
                на четверых — так экономнее
            </>
        ),
        items: [
            { icon: Icon20SmileAddOutline, text: 'Общее Облако для семьи' },
            { icon: Icon20Hearts2Outline, text: 'Семейные папки и альбомы' },
            { icon: Icon20CalendarOutline, text: 'Семейный календарь' },
            { icon: Icon20GovernmentOutline, text: 'Распознавание документов семьи' },
        ],
    },
};

const sendRadar = (action: 'show' | 'close') => {
    sendDwh({ eventCategory: 'family-option-tooltip', action });
    sendQuotaGa({ action: 'tooltip-family-option', label: action });
};

export const FamilyByOptionTooltip = forwardRef<HTMLDivElement, FamilyByOptionTooltipProps>(
    ({ onClose, leftOffset, placement = ETooltipPlacement.topEnd, closeOnOutsideClick = true }, ref) => {
        const dispatch = useDispatch();
        const isMobile = EnvironmentSelectors.isMobile();

        const texts = isMobile ? TEXTS.mobile : TEXTS.desktop;

        const handleOnShow = useCallback(() => {
            sendRadar('show');
        }, [dispatch]);

        const handleClose = useCallback(() => {
            onClose?.(false);
            sendRadar('close');
        }, [onClose]);

        return (
            <FloatingTooltip
                target={ref as RefObject<HTMLDivElement>}
                placement={placement}
                appearance={ETooltipAppearance.blueLight}
                size={ETooltipSize.fit}
                onShow={handleOnShow}
                onClose={handleClose}
                allowFlip={false}
                qaId="family-option-tooltip"
                usePortal
                belowSticky
                isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                contentClassName={styles.content}
                leftOffset={leftOffset}
                closeOnOutsideClick={closeOnOutsideClick && !isMobile}
            >
                <div className={classNames(styles.root, { [styles.rootMobile]: isMobile })}>
                    <Headline weight="1" className={styles.title}>
                        {texts.title}
                    </Headline>
                    <Spacing size={10} />
                    <div className={styles.textWrap}>
                        {texts.items.map((item, index) => (
                            <div key={index} className={styles.item}>
                                <item.icon width={16} height={16} />
                                <span>{item.text}</span>
                            </div>
                        ))}
                    </div>
                    <img src={image} className={classNames(styles.image, { [styles.imageMobile]: isMobile })} alt="Люди" />
                </div>
            </FloatingTooltip>
        );
    }
);

FamilyByOptionTooltip.displayName = 'FamilyByOptionTooltip';
